<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  claimTaxYearConfig,
  useClaimTaxBack,
} from '@/composables/useClaimTaxBack'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import { parseAmountToPence } from '@/helpers/parseAmountToPence'
import {
  CLAIM_FUNNEL_EVENTS,
  trackClaimFunnel,
} from '@/helpers/claimTaxBack/funnel'
import { MAX_MANUAL_GIVING_PENCE } from '@/helpers/claimTaxBack/taxYearConfig'
import type { ClaimTaxRegion } from '@/types/claimTaxBack'
import SaSpinner from '@/components/dashboard/ui/SaSpinner.vue'
import PaidPlanBadge from '@/components/dashboard/ui/PaidPlanBadge.vue'
import ClaimableTotalHint from '@/components/dashboard/claim/ClaimableTotalHint.vue'
import ClaimFaq from '@/components/dashboard/claim/ClaimFaq.vue'
import claimCheckIllustration from '@/assets/images/claim-tax-back-check.svg'

const emit = defineEmits<{
  /** Open the standard upgrade modal (claim-led context comes from the shared profile). */
  upgrade: []
}>()

const { t } = useI18n()
const {
  profile,
  hasCompletedChecker,
  estimate,
  saveCheckerResult,
  resetChecker,
} = useClaimTaxBack()

const yearLabel = claimTaxYearConfig.yearLabel

// One card: the header stays put and the questions expand beneath it once
// they press Start. Three questions, under a minute.
const view = ref<'checker' | 'result'>(
  hasCompletedChecker.value ? 'result' : 'checker'
)
const hasStarted = ref(false)

const region = ref<ClaimTaxRegion | null>(profile.value?.region ?? null)
const incomeBandId = ref<string | null>(profile.value?.incomeBandId ?? null)
const givingInput = ref(
  profile.value?.manualGivingPence != null
    ? (profile.value.manualGivingPence / 100).toFixed(2)
    : ''
)

const isSaving = ref(false)
const formError = ref<string | null>(null)
const resultHeading = ref<HTMLElement | null>(null)
const regionField = ref<HTMLElement | null>(null)

// A saved profile can arrive after mount (module-level load); sync once.
watch(hasCompletedChecker, (completed) => {
  if (completed && view.value !== 'result' && !isSaving.value) {
    region.value = profile.value?.region ?? null
    incomeBandId.value = profile.value?.incomeBandId ?? null
    givingInput.value =
      profile.value?.manualGivingPence != null
        ? (profile.value.manualGivingPence / 100).toFixed(2)
        : ''
    view.value = 'result'
  }
})

const bandsForRegion = computed(() =>
  region.value ? claimTaxYearConfig.bands[region.value] : []
)

// Bands differ per region, so a stale selection must never survive a switch.
watch(region, () => {
  incomeBandId.value = null
})

// The submit button only appears once the last question is on screen.
const canSubmit = computed(() => Boolean(region.value && incomeBandId.value))

const isOwedSomething = computed(
  () => estimate.value !== null && estimate.value.claimablePence > 0
)

const estimateRows = computed(() => {
  if (!estimate.value) return []
  return [
    {
      label: t('Dashboard.ClaimTaxBack.Result.RowGiven'),
      value: formatMoneyFromPence(estimate.value.givenPence),
    },
    {
      label: t('Dashboard.ClaimTaxBack.Result.RowGross'),
      value: formatMoneyFromPence(estimate.value.grossPence),
    },
    {
      label: t('Dashboard.ClaimTaxBack.Result.RowRate'),
      value: `${estimate.value.marginalRatePercent}%`,
    },
    {
      label: t('Dashboard.ClaimTaxBack.Result.RowClaimable'),
      value: formatMoneyFromPence(estimate.value.claimablePence),
    },
  ]
})

const focusResultHeading = async () => {
  await nextTick()
  resultHeading.value?.focus()
}

/** Start button: reveal the first question in the same card and focus it. */
const startChecker = async () => {
  trackClaimFunnel(CLAIM_FUNNEL_EVENTS.CheckerStart, {
    period_key: claimTaxYearConfig.periodKey,
  })
  hasStarted.value = true
  await nextTick()
  // The clicked button is removed with the intro; the browser resets focus
  // to body after that, so wait a frame before moving it to the question.
  requestAnimationFrame(() => regionField.value?.focus())
}

const submitChecker = async () => {
  formError.value = null

  if (!region.value) {
    formError.value = t('Dashboard.ClaimTaxBack.Checker.ErrorRegion')
    return
  }
  if (!incomeBandId.value) {
    formError.value = t('Dashboard.ClaimTaxBack.Checker.ErrorBand')
    return
  }
  const givingPence = parseAmountToPence(givingInput.value)
  if (givingPence === null) {
    formError.value = t('Dashboard.ClaimTaxBack.Checker.ErrorAmount')
    return
  }
  // The API rejects anything above the cap with a 400; stop here instead.
  if (givingPence > MAX_MANUAL_GIVING_PENCE) {
    formError.value = t('Dashboard.ClaimTaxBack.Checker.ErrorAmountTooLarge')
    return
  }

  isSaving.value = true
  try {
    await saveCheckerResult({
      region: region.value,
      incomeBandId: incomeBandId.value,
      manualGivingPence: givingPence,
    })
    trackClaimFunnel(CLAIM_FUNNEL_EVENTS.CheckerComplete, {
      period_key: claimTaxYearConfig.periodKey,
      high_rate: Boolean(profile.value?.highRateTaxpayer),
      claimable_pence: estimate.value?.claimablePence,
    })
    view.value = 'result'
    await focusResultHeading()
  } catch {
    formError.value = t('Dashboard.ClaimTaxBack.Checker.ErrorSave')
  } finally {
    isSaving.value = false
  }
}

const changeAnswers = async () => {
  // Keep the current values on screen for editing; only the saved state
  // clears. They have seen the intro, so the questions open straight away.
  hasStarted.value = true
  view.value = 'checker'
  try {
    await resetChecker()
  } catch {
    // Saving again from the form recovers this; nothing actionable here.
  }
}

const onUpgrade = () => {
  trackClaimFunnel(CLAIM_FUNNEL_EVENTS.UpgradeFromEstimate, {
    period_key: claimTaxYearConfig.periodKey,
    high_rate: Boolean(profile.value?.highRateTaxpayer),
    claimable_pence: estimate.value?.claimablePence,
  })
  emit('upgrade')
}

</script>

<template>
  <div class="sa-claim-checker">
    <!-- One card throughout: header on top, Start beneath it, then the
         questions expand in place so nothing feels like a page change. -->
    <form
      v-if="view === 'checker'"
      class="sa-card sa-card--padded sa-claim-card"
      novalidate
      aria-labelledby="claim-checker-heading"
      :aria-describedby="formError ? 'claim-checker-error' : undefined"
      @submit.prevent="submitChecker"
    >
      <div class="sa-claim-intro">
        <img
          :src="claimCheckIllustration"
          class="sa-claim-intro__illustration"
          alt=""
          width="160"
          height="112"
        />
        <h2 id="claim-checker-heading" class="sa-claim-card__title">
          {{ t('Dashboard.ClaimTaxBack.Checker.Title', { year: yearLabel }) }}
        </h2>
        <p class="sa-claim-card__intro">
          {{ t('Dashboard.ClaimTaxBack.Checker.Intro', { year: yearLabel }) }}
        </p>
      </div>

      <template v-if="!hasStarted">
        <button
          type="button"
          class="sa-btn-purple sa-btn-purple--block"
          @click="startChecker"
        >
          {{ t('Dashboard.ClaimTaxBack.Checker.Start') }}
        </button>
        <p class="sa-claim-card__footnote">
          {{ t('Dashboard.ClaimTaxBack.Checker.Duration') }}
        </p>
      </template>

      <!-- Focus lands on the group from the Start button so the first
           question is announced. A legend cannot take focus in Chrome. -->
      <fieldset
        v-if="hasStarted"
        ref="regionField"
        class="sa-claim-field"
        tabindex="-1"
      >
        <legend class="sa-claim-field__legend">
          {{ t('Dashboard.ClaimTaxBack.Checker.RegionLegend') }}
        </legend>
        <p class="sa-claim-field__help" id="claim-region-help">
          {{ t('Dashboard.ClaimTaxBack.Checker.RegionHelp') }}
        </p>
        <div class="sa-claim-options" aria-describedby="claim-region-help">
          <label
            class="sa-claim-option"
            :class="{ 'sa-claim-option--selected': region === 'ruk' }"
          >
            <input
              v-model="region"
              class="sa-sr-only"
              type="radio"
              name="claim-region"
              value="ruk"
            />
            <span>{{ t('Dashboard.ClaimTaxBack.Checker.RegionRuk') }}</span>
          </label>
          <label
            class="sa-claim-option"
            :class="{ 'sa-claim-option--selected': region === 'scotland' }"
          >
            <input
              v-model="region"
              class="sa-sr-only"
              type="radio"
              name="claim-region"
              value="scotland"
            />
            <span>
              {{ t('Dashboard.ClaimTaxBack.Checker.RegionScotland') }}
            </span>
          </label>
        </div>
      </fieldset>

      <fieldset v-if="hasStarted && region" class="sa-claim-field">
        <legend class="sa-claim-field__legend">
          {{
            t('Dashboard.ClaimTaxBack.Checker.IncomeLegend', { year: yearLabel })
          }}
        </legend>
        <p class="sa-claim-field__help" id="claim-income-help">
          {{ t('Dashboard.ClaimTaxBack.Checker.IncomeHelp') }}
        </p>
        <div class="sa-claim-options" aria-describedby="claim-income-help">
          <label
            v-for="band in bandsForRegion"
            :key="band.id"
            class="sa-claim-option"
            :class="{ 'sa-claim-option--selected': incomeBandId === band.id }"
          >
            <input
              v-model="incomeBandId"
              class="sa-sr-only"
              type="radio"
              name="claim-income-band"
              :value="band.id"
            />
            <span>{{ band.label }}</span>
          </label>
        </div>
      </fieldset>

      <div v-if="hasStarted && canSubmit" class="sa-claim-field">
        <label class="sa-claim-field__legend" for="claim-giving-amount">
          {{
            t('Dashboard.ClaimTaxBack.Checker.GivingLabel', { year: yearLabel })
          }}
        </label>
        <p class="sa-claim-field__help" id="claim-giving-help">
          {{ t('Dashboard.ClaimTaxBack.Checker.GivingHelp') }}
        </p>
        <div class="sa-claim-amount">
          <span class="sa-claim-amount__symbol" aria-hidden="true">£</span>
          <input
            id="claim-giving-amount"
            v-model="givingInput"
            class="sa-input sa-claim-amount__input"
            type="text"
            inputmode="decimal"
            autocomplete="off"
            :placeholder="t('Dashboard.ClaimTaxBack.Checker.GivingPlaceholder')"
            :aria-invalid="formError ? true : undefined"
            :aria-describedby="
              formError
                ? 'claim-giving-help claim-checker-error'
                : 'claim-giving-help'
            "
            aria-required="true"
          />
        </div>
      </div>

      <p v-if="formError" id="claim-checker-error" class="sa-field-error" role="alert">
        {{ formError }}
      </p>

      <button
        v-if="hasStarted && canSubmit"
        type="submit"
        class="sa-btn-purple sa-btn-purple--block"
        :disabled="isSaving"
        :aria-busy="isSaving"
      >
        <SaSpinner v-if="isSaving" size="button" />
        {{ t('Dashboard.ClaimTaxBack.Checker.Submit') }}
      </button>
    </form>

    <div v-else-if="estimate" class="sa-claim-result">
      <!-- Owed: the estimate card is the donor's number and working only; the
           sell lives in its own card below so the figure stays untainted. -->
      <template v-if="isOwedSomething">
        <section
          class="sa-card sa-card--padded sa-claim-card"
          aria-labelledby="claim-result-heading"
        >
          <div class="sa-claim-result__hero">
            <p class="sa-claim-result__eyebrow">
              {{
                t('Dashboard.ClaimTaxBack.Result.Eyebrow', { year: yearLabel })
              }}
            </p>
            <h2
              id="claim-result-heading"
              ref="resultHeading"
              class="sa-claim-result__headline"
              tabindex="-1"
            >
              <span class="sa-claim-result__label">
                {{ t('Dashboard.ClaimTaxBack.Result.Headline') }}
              </span>
              <span class="sa-sr-only">
                {{ formatMoneyFromPence(estimate.claimablePence) }}
              </span>
            </h2>
            <!-- Hint sits next to the total: this figure is an estimate. -->
            <ClaimableTotalHint placement="bottom">
              <span class="sa-claim-result__amount" aria-hidden="true">
                {{ formatMoneyFromPence(estimate.claimablePence) }}
              </span>
            </ClaimableTotalHint>
          </div>

          <!-- Compact CTA while the figure is on screen. The descriptive
               upgrade card below keeps the full pitch and subscription terms. -->
          <div class="sa-claim-result__claim-cta">
            <p id="claim-inline-cta-copy" class="sa-claim-result__claim-cta-copy">
              {{ t('Dashboard.ClaimTaxBack.Result.ClaimCtaBody') }}
            </p>
            <button
              type="button"
              class="sa-btn-purple sa-btn-purple--compact"
              aria-describedby="claim-inline-cta-copy"
              @click="onUpgrade"
            >
              {{ t('Dashboard.ClaimTaxBack.Result.ClaimCta') }}
              <PaidPlanBadge />
            </button>
          </div>

          <h3 class="sa-claim-result__subheading">
            {{ t('Dashboard.ClaimTaxBack.Result.WorkingHeading') }}
          </h3>
          <dl class="sa-claim-working">
            <div
              v-for="row in estimateRows"
              :key="row.label"
              class="sa-claim-working__row"
            >
              <dt>{{ row.label }}</dt>
              <dd>{{ row.value }}</dd>
            </div>
          </dl>
          <!-- At 40% the claim equals the charity's Gift Aid; unexplained, the
               two identical figures read as a bug. -->
          <p class="sa-claim-result__explain">
            {{
              t('Dashboard.ClaimTaxBack.Result.Explain', {
                amount: formatMoneyFromPence(estimate.claimablePence),
              })
            }}
          </p>

          <button
            type="button"
            class="sa-claim-link-btn"
            @click="changeAnswers"
          >
            {{ t('Dashboard.ClaimTaxBack.Result.ChangeAnswers') }}
          </button>
        </section>

        <!-- Claim-focused pitch only: the upgrade modal lists the full
             feature set one click later. -->
        <section
          class="sa-card sa-card--padded sa-claim-card sa-claim-upsell"
          aria-labelledby="claim-upsell-heading"
        >
          <h2 id="claim-upsell-heading" class="sa-claim-upsell__title">
            {{ t('Dashboard.ClaimTaxBack.Result.UpsellTitle') }}
          </h2>
          <p class="sa-claim-upsell__body">
            {{ t('Dashboard.ClaimTaxBack.Result.UpsellBody') }}
          </p>
          <!-- Same Upgrade button as the dashboard so this reads as the one
               account upgrade, not a separate purchase. The donor's figure is
               in the title above; the price is in the terms line below. -->
          <button
            type="button"
            class="sa-btn-purple sa-btn-purple--block"
            @click="onUpgrade"
          >
            {{ t('Dashboard.Home.UpgradeCta') }}
            <PaidPlanBadge />
          </button>
          <!-- DMCC/CMA subscription terms: charged today, auto-renews, cancellable. -->
          <p class="sa-claim-card__footnote">
            {{ t('Dashboard.UpgradeModal.StripeFooter') }}
          </p>
        </section>

        <!-- Same common questions as the paid guide, including why the
             claimable figure is an "up to" estimate that HMRC may change. -->
        <ClaimFaq />
      </template>

      <section
        v-else
        class="sa-card sa-card--padded sa-claim-card"
        aria-labelledby="claim-result-heading"
      >
        <h2
          id="claim-result-heading"
          ref="resultHeading"
          class="sa-claim-result__headline"
          tabindex="-1"
        >
          {{ t('Dashboard.ClaimTaxBack.NothingOwed.Title') }}
        </h2>
        <!-- Owed nothing: say so plainly, no claim upsell (requirement). -->
        <p class="sa-claim-result__explain">
          {{
            t('Dashboard.ClaimTaxBack.NothingOwed.Body', { year: yearLabel })
          }}
        </p>
        <div class="sa-claim-record-offer">
          <h3 class="sa-claim-record-offer__title">
            {{ t('Dashboard.ClaimTaxBack.NothingOwed.RecordTitle') }}
          </h3>
          <p class="sa-claim-record-offer__body">
            {{ t('Dashboard.ClaimTaxBack.NothingOwed.RecordBody') }}
          </p>
          <button
            type="button"
            class="sa-claim-link-btn"
            @click="onUpgrade"
          >
            {{ t('Dashboard.ClaimTaxBack.NothingOwed.RecordCta') }}
          </button>
        </div>
        <button type="button" class="sa-claim-link-btn" @click="changeAnswers">
          {{ t('Dashboard.ClaimTaxBack.Result.ChangeAnswers') }}
        </button>
      </section>
    </div>
  </div>
</template>
